import { SignJWT } from 'jose'

export interface SessionUser {
  id: string
  email: string
  name: string
  role: 'admin'
}

export interface Session {
  user: SessionUser | null
  expires: number | null
  token: string | null
}

const SESSION_COOKIE_NAME = 'pranala_session'
const SESSION_EXPIRY_HOURS = 24 // 24 hours

// Lazy initialization of JWT secret to prevent module-load-time errors
let _jwtSecret: Uint8Array | null = null

function getJwtSecret(): Uint8Array {
  if (_jwtSecret) {
    return _jwtSecret
  }

  const sessionSecret = import.meta.env.VITE_SESSION_SECRET
  if (!sessionSecret) {
    throw new Error('SESSION_SECRET environment variable is required')
  }

  _jwtSecret = new TextEncoder().encode(sessionSecret)
  return _jwtSecret
}

export class SessionManager {
  /**
   * Create JWT session and set cookie
   * @param user - User object
   */
  static async createSession(user: SessionUser): Promise<void> {
    try {
      const expires = Date.now() + (SESSION_EXPIRY_HOURS * 60 * 60 * 1000)
      
      // Create JWT token
      const token = await new SignJWT({
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role
      })
        .setProtectedHeader({ alg: 'HS256' })
        .setIssuedAt()
        .setExpirationTime(new Date(expires))
        .sign(getJwtSecret())

      const session: Session = {
        user,
        expires,
        token
      }

      // Set secure cookie
      if (typeof window !== 'undefined') {
        document.cookie = this.getSecureCookieString(session, expires)
      }
    } catch (error) {
      console.error('Failed to create session:', error)
      throw new Error('Session creation failed')
    }
  }

  /**
   * Get current session from cookie
   * @returns Session object or null
   */
  static async getSession(): Promise<Session | null> {
    if (typeof window === 'undefined') {
      return null
    }

    try {
      const cookies = document.cookie.split(';')
      let sessionCookie = ''

      for (const cookie of cookies) {
        const trimmed = cookie.trim()
        const eqIndex = trimmed.indexOf('=')
        if (eqIndex === -1) continue
        const name = trimmed.substring(0, eqIndex)
        const value = trimmed.substring(eqIndex + 1)
        if (name === SESSION_COOKIE_NAME) {
          sessionCookie = value
          break
        }
      }

      if (!sessionCookie) {
        return null
      }

      // Parse session data
      let sessionData: Session
      try {
        sessionData = JSON.parse(decodeURIComponent(sessionCookie)) as Session
      } catch {
        // Cookie might be malformed, but don't clear it - just return null
        console.warn('Failed to parse session cookie')
        return null
      }

      // Check if session is expired
      if (sessionData.expires && sessionData.expires < Date.now()) {
        this.clearSession()
        return null
      }

      // On client-side, trust the session data (JWT was signed by server)
      // JWT verification only happens server-side in validateServerSession
      if (sessionData.user && sessionData.token) {
        return {
          user: sessionData.user,
          expires: sessionData.expires,
          token: sessionData.token
        }
      }

      return null
    } catch (error) {
      console.error('Failed to get session:', error)
      return null
    }
  }

  /**
   * Clear session
   */
  static clearSession(): void {
    if (typeof window !== 'undefined') {
      document.cookie = `${SESSION_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax; ${
        import.meta.env.PROD ? 'Secure;' : ''
      }`
    }
  }

  /**
   * Check if user is authenticated
   * @returns Boolean
   */
  static async isAuthenticated(): Promise<boolean> {
    const session = await this.getSession()
    return session !== null && session.user !== null
  }

  /**
   * Get current user
   * @returns User object or null
   */
  static async getCurrentUser(): Promise<SessionUser | null> {
    const session = await this.getSession()
    return session?.user || null
  }

  /**
   * Refresh session (extend expiry)
   * @returns Boolean success
   */
  static async refreshSession(): Promise<boolean> {
    try {
      const session = await this.getSession()
      if (!session || !session.user) {
        return false
      }

      await this.createSession(session.user)
      return true
    } catch (error) {
      console.error('Failed to refresh session:', error)
      return false
    }
  }

  /**
   * Create secure cookie string
   * @param session - Session data
   * @param expires - Expiry timestamp
   * @returns Cookie string
   */
  private static getSecureCookieString(session: Session, expires: number): string {
    const encodedSession = encodeURIComponent(JSON.stringify({
      user: session.user,
      expires: session.expires,
      token: session.token
    }))
    
    const expiryDate = new Date(expires).toUTCString()
    
    return `${SESSION_COOKIE_NAME}=${encodedSession}; expires=${expiryDate}; path=/; SameSite=Lax; ${
      import.meta.env.PROD ? 'Secure; HttpOnly;' : ''
    }`
  }
}
